import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bot, PhoneCall, Play, Pause, TestTube2, Sparkles, X, ShieldCheck, AlertTriangle, RefreshCw } from "lucide-react";

/**
 * LeadsAIDialerPanel — attaches to the /leads page.
 * Only renders when a specific campaign is selected in the filter.
 * Shows AI SDR + Caller ID pickers, prompt editor, dry-run, test call,
 * state toggle, and "Start dialing" batch button.
 *
 * Props:
 *   - campaignId, campaignName (currently filtered campaign)
 *   - onClose (unmount)
 */
export default function LeadsAIDialerPanel({ campaignId, campaignName, onClose }) {
  const [cfg, setCfg] = useState(null);
  const [agents, setAgents] = useState([]);
  const [numbers, setNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testCallPhone, setTestCallPhone] = useState("");
  const [dryRunOutput, setDryRunOutput] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [queueSnap, setQueueSnap] = useState({ pending: 0, in_progress: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, n] = await Promise.all([
        api.get(`/campaigns/${campaignId}/ai-config`),
        api.get(`/admin/growth/voice/numbers`),
      ]);
      setCfg(c.data);
      setNumbers(n.data.items || []);
      setQueueSnap(c.data.queue || { pending: 0, in_progress: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);

  const pollQueue = useCallback(async () => {
    try {
      const { data } = await api.get(`/campaigns/${campaignId}/ai/queue`);
      setQueueSnap({ pending: data.pending, in_progress: data.dialing });
    } catch { /* silent — queue endpoint is best-effort */ }
  }, [campaignId]);

  useEffect(() => {
    if (cfg?.ai?.state !== "live") return;
    const t = setInterval(pollQueue, 8000);
    return () => clearInterval(t);
  }, [cfg?.ai?.state, pollQueue]);

  const patch = (field, value) => setCfg((c) => ({ ...c, ai: { ...c.ai, [field]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/campaigns/${campaignId}/ai-config`, cfg.ai);
      setCfg((c) => ({ ...c, ai: data.ai_config }));
      toast.success("AI config saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const runDry = async () => {
    setDryRunOutput(null);
    try {
      const { data } = await api.post(`/campaigns/${campaignId}/ai/dry-run`, {});
      setDryRunOutput(data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const runTestCall = async () => {
    if (!testCallPhone || !testCallPhone.startsWith("+")) {
      return toast.error("Enter your phone in E.164 format (e.g. +919822846719)");
    }
    try {
      await api.post(`/campaigns/${campaignId}/ai/test-call`, { phone: testCallPhone, name: "Admin" });
      toast.success("📞 Test call fired — pick up your phone");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const setState = async (state) => {
    try {
      await api.post(`/campaigns/${campaignId}/ai/state`, { state });
      toast.success(`State → ${state}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const startDialing = async () => {
    if (!window.confirm(`Start batch dialing leads in "${campaignName}"?`)) return;
    try {
      const { data } = await api.post(`/campaigns/${campaignId}/ai/start-dialing`);
      toast.success(`Queued ${data.queued} of ${data.eligible_total} eligible lead(s)`);
      pollQueue();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const stopDialing = async () => {
    try {
      const { data } = await api.post(`/campaigns/${campaignId}/ai/stop-dialing`);
      toast.success(`Cancelled ${data.cancelled} pending dial(s)`);
      pollQueue();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const generateWithAI = async () => {
    setDrafting(true);
    try {
      const brief = window.prompt("Describe your campaign in 1-2 lines (industry, offer, target):",
        `Outbound SDR calls for ${campaignName}. Book demo meetings.`);
      if (!brief) { setDrafting(false); return; }
      // Reuse the WhatsApp/AI-write endpoint for text generation.
      const { data } = await api.post(`/admin/sbt/service-kb/ai-write`, {
        field: "call_script",
        prompt: brief,
        context: `Generate a warm, natural outbound SDR script for a US-based B2B campaign. Include: opener, one qualifying question, and a soft demo-book close. Keep it under 800 words. Use {{prospect_name}} and {{prospect_company}} placeholders.`,
      });
      patch("system_prompt", data.text || data.result || "");
      toast.success("Draft ready — review and save");
    } catch (e) {
      toast.error("AI-draft failed: " + (e.response?.data?.detail || e.message));
    } finally { setDrafting(false); }
  };

  if (loading) return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
      <div className="bg-white p-6 rounded shadow text-sm">Loading AI config…</div>
    </div>
  );

  const ai = cfg.ai;
  const stateColor = { draft: "bg-slate-200 text-slate-700",
                       tested: "bg-blue-100 text-blue-800",
                       live: "bg-emerald-100 text-emerald-800",
                       paused: "bg-amber-100 text-amber-800" }[ai.state] || "bg-slate-200";

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="ai-dialer-panel">
      <div className="flex-1 bg-black/40" onClick={onClose}/>
      <div className="w-[720px] max-w-full bg-white shadow-2xl overflow-y-auto flex flex-col">
        <header className="p-5 border-b flex items-start justify-between gap-3 sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600 flex items-center gap-1">
              <Bot size={12}/> AI SDR Dialer
            </div>
            <h3 className="text-lg font-bold text-slate-900">{campaignName}</h3>
            <div className="mt-1 flex items-center gap-2">
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${stateColor}`}
                    data-testid="ai-dialer-state">
                {ai.state}
              </span>
              {ai.state === "live" && (
                <span className="text-xs text-slate-600 flex items-center gap-1">
                  📞 Dialing: <b>{queueSnap.in_progress}</b> · Queue: <b>{queueSnap.pending}</b>
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl px-1">
            <X size={18}/>
          </button>
        </header>

        {/* 1. AI SDR + Caller ID + basics */}
        <section className="p-5 border-b space-y-3">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-600">1 · Configure SDR</div>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs">
              <div className="font-semibold text-slate-700 mb-1">AI SDR (voice)</div>
              <select value={ai.voice_id} onChange={(e) => patch("voice_id", e.target.value)}
                      className="w-full text-sm px-3 py-2 border border-slate-200 rounded"
                      data-testid="ai-dialer-voice">
                <option value="21m00Tcm4TlvDq8ikWAM">Rachel · US female · warm</option>
                <option value="9BWtsMINqrJLrRacOk9x">Aria · US female · analyst</option>
                <option value="EXAVITQu4vr4xnSDxMaL">Bella · US female · younger</option>
                <option value="ErXwobaYiN019PkySvjV">Antoni · US male · natural</option>
                <option value="TxGEqnHWrfWFTfGW9XjX">Josh · US male · deep</option>
              </select>
            </label>
            <label className="text-xs">
              <div className="font-semibold text-slate-700 mb-1">Caller ID</div>
              <select value={ai.voice_number_id} onChange={(e) => patch("voice_number_id", e.target.value)}
                      className="w-full text-sm px-3 py-2 border border-slate-200 rounded"
                      data-testid="ai-dialer-number">
                <option value="">— Choose a number —</option>
                {numbers.filter((n) => n.status === "active").map((n) =>
                  <option key={n.id} value={n.id}>{n.country} · {n.phone_number}</option>)}
              </select>
            </label>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <label className="text-xs">
              <div className="font-semibold text-slate-700 mb-1">Language</div>
              <select value={ai.language} onChange={(e) => patch("language", e.target.value)}
                      className="w-full text-sm px-3 py-2 border border-slate-200 rounded">
                <option value="en">English</option>
                <option value="hi">Hindi</option>
                <option value="es">Spanish</option>
                <option value="de">German</option>
                <option value="fr">French</option>
              </select>
            </label>
            <label className="text-xs">
              <div className="font-semibold text-slate-700 mb-1">Max attempts (per lead)</div>
              <input type="number" min={1} max={5} value={ai.max_attempts}
                     onChange={(e) => patch("max_attempts", parseInt(e.target.value) || 3)}
                     className="w-full text-sm px-3 py-2 border border-slate-200 rounded"/>
            </label>
            <label className="text-xs">
              <div className="font-semibold text-slate-700 mb-1">Cooldown (hours)</div>
              <input type="number" min={1} max={168} value={ai.cooldown_hours}
                     onChange={(e) => patch("cooldown_hours", parseInt(e.target.value) || 24)}
                     className="w-full text-sm px-3 py-2 border border-slate-200 rounded"/>
            </label>
          </div>
        </section>

        {/* 2. Prompt editor */}
        <section className="p-5 border-b space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-600">2 · Calling script</div>
            <button onClick={generateWithAI} disabled={drafting}
                    className="text-xs px-2 py-1 border border-purple-300 bg-purple-50 text-purple-700 rounded hover:bg-purple-100 flex items-center gap-1 disabled:opacity-50"
                    data-testid="ai-dialer-generate">
              <Sparkles size={11}/> {drafting ? "Drafting…" : "Generate with AI"}
            </button>
          </div>
          <label className="text-xs">
            <div className="font-semibold text-slate-700 mb-1">First message (opener)</div>
            <input value={ai.first_message} onChange={(e) => patch("first_message", e.target.value)}
                   placeholder="Hi {{prospect_name}}, do you have 90 seconds?"
                   className="w-full text-sm px-3 py-2 border border-slate-200 rounded font-mono"
                   data-testid="ai-dialer-first-msg"/>
          </label>
          <label className="text-xs">
            <div className="font-semibold text-slate-700 mb-1 flex items-center gap-1">
              System prompt (the SDR&apos;s brain)
              <span className="text-slate-400 font-normal">— use {"{{prospect_name}}"}, {"{{prospect_company}}"}, {"{{prospect_email}}"}</span>
            </div>
            <textarea rows={14} value={ai.system_prompt}
                      onChange={(e) => patch("system_prompt", e.target.value)}
                      placeholder="You are a friendly SDR calling {{prospect_name}}…"
                      className="w-full text-xs px-3 py-2 border border-slate-200 rounded font-mono leading-relaxed"
                      data-testid="ai-dialer-prompt"/>
          </label>
          <div className="text-[11px] text-slate-500 flex items-center gap-1">
            <ShieldCheck size={11} className="text-emerald-600"/>
            Compliance disclaimer + DNC opt-out language are auto-injected on save.
          </div>
        </section>

        {/* 3. Dry run + Test call */}
        <section className="p-5 border-b space-y-3">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-600">3 · Dry run &amp; test</div>
          <div className="flex flex-wrap gap-2">
            <button onClick={runDry}
                    className="text-xs px-3 py-2 border border-slate-300 rounded bg-white hover:bg-slate-50 flex items-center gap-1"
                    data-testid="ai-dialer-dry-run">
              <Sparkles size={12}/> Dry run (preview only)
            </button>
            <div className="flex items-center gap-2 flex-1 min-w-[280px]">
              <input value={testCallPhone} onChange={(e) => setTestCallPhone(e.target.value)}
                     placeholder="Your phone (e.g. +919822846719)"
                     className="flex-1 text-xs px-3 py-2 border border-slate-200 rounded font-mono"
                     data-testid="ai-dialer-test-phone"/>
              <button onClick={runTestCall} disabled={!ai.el_agent_id || !ai.voice_number_id}
                      className="text-xs px-3 py-2 bg-blue-600 text-white rounded font-semibold hover:bg-blue-700 disabled:opacity-40 flex items-center gap-1"
                      data-testid="ai-dialer-test-call">
                <TestTube2 size={12}/> Test call
              </button>
            </div>
          </div>
          {dryRunOutput && (
            <div className="border border-slate-200 rounded p-3 bg-slate-50 space-y-2 text-xs">
              {(dryRunOutput.warnings || []).length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-amber-900">
                  <div className="font-semibold flex items-center gap-1 mb-0.5"><AlertTriangle size={11}/> Warnings</div>
                  <ul className="list-disc list-inside space-y-0.5">
                    {dryRunOutput.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
              <div>
                <div className="font-semibold text-slate-700 mb-0.5">First message (rendered):</div>
                <div className="font-mono bg-white border border-slate-200 rounded p-2 text-slate-900">
                  {dryRunOutput.rendered_first_message || <i className="text-slate-400">(empty)</i>}
                </div>
              </div>
              <div>
                <div className="font-semibold text-slate-700 mb-0.5">System prompt (first 800 chars):</div>
                <div className="font-mono bg-white border border-slate-200 rounded p-2 text-slate-700 whitespace-pre-wrap">
                  {(dryRunOutput.rendered_system_prompt || "").slice(0, 800)}
                  {(dryRunOutput.rendered_system_prompt || "").length > 800 && "…"}
                </div>
              </div>
            </div>
          )}
        </section>

        {/* 4. State + Go Live */}
        <section className="p-5 border-b space-y-3">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-600">4 · State &amp; live dialing</div>
          <div className="text-xs text-slate-500">
            Flow: <b>draft</b> → save config → run a test call → state auto-flips to <b>tested</b> → click <b>Go Live</b> → click <b>Start Dialing</b>.
          </div>
          <div className="flex flex-wrap gap-2">
            {ai.state !== "live" && (
              <button onClick={() => setState("live")} disabled={ai.state === "draft"}
                      className="text-xs px-3 py-2 bg-emerald-600 text-white rounded font-semibold hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-1"
                      data-testid="ai-dialer-go-live">
                <Play size={12}/> Go Live
              </button>
            )}
            {ai.state === "live" && (
              <>
                <button onClick={() => setState("paused")}
                        className="text-xs px-3 py-2 bg-amber-500 text-white rounded font-semibold hover:bg-amber-600 flex items-center gap-1"
                        data-testid="ai-dialer-pause">
                  <Pause size={12}/> Pause
                </button>
                <button onClick={startDialing}
                        className="text-xs px-3 py-2 bg-blue-600 text-white rounded font-semibold hover:bg-blue-700 flex items-center gap-1"
                        data-testid="ai-dialer-start">
                  <PhoneCall size={12}/> Start dialing queue
                </button>
                <button onClick={stopDialing}
                        className="text-xs px-3 py-2 border border-slate-300 rounded bg-white hover:bg-slate-50 flex items-center gap-1">
                  <X size={12}/> Cancel pending
                </button>
              </>
            )}
            {ai.state === "paused" && (
              <button onClick={() => setState("live")}
                      className="text-xs px-3 py-2 bg-emerald-600 text-white rounded font-semibold hover:bg-emerald-700 flex items-center gap-1">
                <Play size={12}/> Resume
              </button>
            )}
            <button onClick={pollQueue}
                    className="text-xs px-3 py-2 border border-slate-300 rounded bg-white hover:bg-slate-50 flex items-center gap-1">
              <RefreshCw size={11}/> Refresh queue
            </button>
          </div>
          {ai.state === "live" && queueSnap.pending > 0 && (
            <div className="text-xs text-slate-600 bg-emerald-50 border border-emerald-200 rounded p-2">
              🟢 Dialer is running — <b>{queueSnap.pending}</b> lead(s) in queue, dialing one every ~20 seconds.
            </div>
          )}
        </section>

        {/* Footer */}
        <footer className="p-4 border-t flex justify-between items-center sticky bottom-0 bg-white">
          <div className="text-[11px] text-slate-400">
            EL agent: {ai.el_agent_id ? <code>{ai.el_agent_id.slice(0, 24)}…</code> : <i>not yet created</i>}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="text-sm px-4 py-2 border border-slate-200 rounded text-slate-600">Close</button>
            <button onClick={save} disabled={saving}
                    className="text-sm px-4 py-2 bg-amber-500 text-white rounded font-semibold hover:bg-amber-600 disabled:opacity-50"
                    data-testid="ai-dialer-save">
              {saving ? "Saving…" : "Save config"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
